import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useCart } from '../../../context/CartContext';

const ProductInfo = ({ product, quantity, onQuantityChange, onAddToCart }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const handleIncrement = () => {
    if (quantity < product?.maxQuantity) {
      onQuantityChange(quantity + 1);
    }
  };

  const handleDecrement = () => {
    if (quantity > 1) {
      onQuantityChange(quantity - 1);
    }
  };

  const handleBuyNow = () => {
    navigate('/payment-instructions', {
      state: {
        product: product,
        quantity: quantity
      }
    });
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    // Show success feedback
    if (onAddToCart) {
      onAddToCart();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="px-3 py-1 rounded-lg bg-primary/10 border border-primary/30 text-primary text-sm font-medium caption">
            {product?.category}
          </span>
          {product?.isNew && (
            <span className="px-3 py-1 rounded-lg bg-accent/10 border border-accent/30 text-accent text-sm font-medium caption">
              NEW
            </span>
          )}
        </div>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">
          {product?.name}
        </h1>
        <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
          {product?.shortDescription}
        </p>
      </div>
      <div className="flex items-baseline gap-4">
        <span className="text-4xl md:text-5xl font-bold text-primary data-text">
          ${product?.price}
        </span>
        {product?.originalPrice && (
          <span className="text-xl md:text-2xl text-muted-foreground line-through data-text">
            ${product?.originalPrice}
          </span>
        )}
      </div>
      <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/50 border border-border">
        <div className="flex items-center gap-2">
          <Icon 
            name={product?.inStock ? "CheckCircle2" : "XCircle"} 
            size={20} 
            color={product?.inStock ? "var(--color-success)" : "var(--color-error)"} 
          />
          <span className={`font-medium ${product?.inStock ? 'text-success' : 'text-error'}`}>
            {product?.inStock ? 'In Stock' : 'Out of Stock'}
          </span>
        </div>
        {product?.inStock && (
          <span className="text-muted-foreground caption">
            {product?.stockCount} units available
          </span>
        )}
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Icon name="Package" size={18} color="var(--color-primary)" />
          <span className="text-foreground">
            <span className="font-medium">Dosage:</span> {product?.dosage}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Icon name="Beaker" size={18} color="var(--color-primary)" />
          <span className="text-foreground">
            <span className="font-medium">Lab Tested:</span> {product?.purity}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Icon name="Snowflake" size={18} color="var(--color-primary)" />
          <span className="text-foreground">
            <span className="font-medium">Storage:</span> {product?.storage}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-4 p-6 rounded-xl bg-card border border-border">
        <span className="text-foreground font-medium">Quantity:</span>
        <div className="flex items-center gap-3">
          <button
            onClick={handleDecrement}
            disabled={quantity <= 1}
            className="w-10 h-10 rounded-lg bg-muted border border-border flex items-center justify-center transition-all duration-250 hover:bg-primary/20 hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Decrease quantity"
          >
            <Icon name="Minus" size={18} color="var(--color-primary)" />
          </button>
          <span className="w-16 text-center text-xl font-bold text-foreground data-text">
            {quantity}
          </span>
          <button
            onClick={handleIncrement}
            disabled={quantity >= product?.maxQuantity}
            className="w-10 h-10 rounded-lg bg-muted border border-border flex items-center justify-center transition-all duration-250 hover:bg-primary/20 hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Increase quantity"
          >
            <Icon name="Plus" size={18} color="var(--color-primary)" />
          </button>
        </div>
        <span className="text-muted-foreground caption ml-auto">
          Max: {product?.maxQuantity}
        </span>
      </div>
      <div className="space-y-3">
        <Button
          variant="outline"
          size="lg"
          fullWidth
          iconName="ShoppingCart"
          iconPosition="left"
          onClick={handleAddToCart}
          disabled={!product?.inStock}
        >
          Add to Cart
        </Button>
        <Button
          variant="default"
          size="lg"
          fullWidth
          iconName="CreditCard"
          iconPosition="left"
          onClick={handleBuyNow}
          disabled={!product?.inStock}
        >
          Buy Now - ${(product?.price * quantity)?.toFixed(2)}
        </Button>
      </div>
      
      <div className="p-4 rounded-xl bg-accent/10 border border-accent/30">
        <div className="flex items-start gap-3">
          <Icon name="MessageSquare" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-1" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-accent">After Payment Confirmation</p>
            <p className="text-sm text-foreground">
              Please text <span className="font-bold text-primary">801-810-4671</span> to confirm your order
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-muted/50 border border-border">
          <Icon name="Shield" size={18} color="var(--color-success)" />
          <span className="text-sm text-foreground caption">Secure</span>
        </div>
        <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-muted/50 border border-border">
          <Icon name="Truck" size={18} color="var(--color-success)" />
          <span className="text-sm text-foreground caption">Fast Ship</span>
        </div>
        <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-muted/50 border border-border">
          <Icon name="RotateCcw" size={18} color="var(--color-success)" />
          <span className="text-sm text-foreground caption">Returns</span>
        </div>
      </div>
    </div>
  );
};

export default ProductInfo;